var _excluded = ["disabled", "disableFirstOption", "errorMessage", "fullWidth", "fromFormat", "helperText", "helperTextPlacement", "label", "minutesIncrementStep", "onChange", "name", "required", "showError", "size", "toFormat", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import FormControl from './FormControl';
import Select from './Select';
import { getHourOptions, getMeridianOptions, getMinuteOptions, parseTimeStrByFormat } from '../utils/helpers';
import { StyledContainer, StyledSelect, StyledSpacer } from './TimeInput.styled';
function TimeInput(_ref) {
  var disabled = _ref.disabled,
    disableFirstOption = _ref.disableFirstOption,
    errorMessage = _ref.errorMessage,
    fullWidth = _ref.fullWidth,
    fromFormat = _ref.fromFormat,
    helperText = _ref.helperText,
    helperTextPlacement = _ref.helperTextPlacement,
    label = _ref.label,
    minutesIncrementStep = _ref.minutesIncrementStep,
    onChange = _ref.onChange,
    name = _ref.name,
    required = _ref.required,
    showError = _ref.showError,
    size = _ref.size,
    toFormat = _ref.toFormat,
    value = _ref.value,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var inputRef = useRef();
  var _useState = useState({
      hour: '',
      minute: '',
      meridian: ''
    }),
    values = _useState[0],
    setValues = _useState[1];

  // Set the initial values
  useEffect(function () {
    if (value) {
      var dateObj = parseTimeStrByFormat(value, fromFormat);
      setValues({
        hour: dateObj.format('hh'),
        minute: dateObj.format('mm'),
        meridian: dateObj.format('a')
      });
    }
  }, [fromFormat, value]);
  var hourOptions = getHourOptions(disableFirstOption);
  var meridianOptions = getMeridianOptions(disableFirstOption);
  var minuteOptions = getMinuteOptions(0, minutesIncrementStep === 5 ? 55 : 59, minutesIncrementStep, disableFirstOption);
  var handleChange = function handleChange(e, type) {
    var _extends2;
    var newValues = _extends({}, values, (_extends2 = {}, _extends2[type] = e.target.value, _extends2));
    var dateObj = parseTimeStrByFormat(newValues.hour + ":" + newValues.minute + newValues.meridian, 'hh:mma');
    var dateStr = dateObj.format(toFormat);
    var newValue = dateStr === 'Invalid Date' ? '' : dateStr;
    inputRef.current.value = newValue;
    var evt = new Event('change', {
      bubbles: true,
      cancelable: false
    });
    inputRef.current.dispatchEvent(evt);
    setValues(newValues);
    onChange(evt);
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    name: name,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: "normal"
  }, /*#__PURE__*/React.createElement("input", {
    defaultValue: rest.defaultValue || value,
    name: name,
    ref: inputRef,
    style: {
      height: 0,
      width: 0,
      appearance: 'none',
      border: 'none',
      outline: 'none',
      margin: 0,
      padding: 0
    }
  }), /*#__PURE__*/React.createElement(StyledContainer, {
    size: size
  }, /*#__PURE__*/React.createElement(StyledSelect, {
    required: required,
    size: size,
    value: inputRef.current ? inputRef.current.value : value
  }, /*#__PURE__*/React.createElement(Select, {
    disabled: disabled,
    name: name + "_hours",
    placeholder: "HH",
    required: required,
    native: true,
    options: hourOptions,
    value: values.hour,
    onChange: function onChange(e) {
      handleChange(e, 'hour');
    }
  })), /*#__PURE__*/React.createElement(StyledSpacer, null, ":"), /*#__PURE__*/React.createElement(StyledSelect, {
    required: required,
    size: size,
    value: inputRef.current ? inputRef.current.value : value
  }, /*#__PURE__*/React.createElement(Select, {
    disabled: disabled,
    name: name + "_minutes",
    placeholder: "MM",
    required: required,
    native: true,
    options: minuteOptions,
    value: values.minute,
    onChange: function onChange(e) {
      handleChange(e, 'minute');
    }
  })), /*#__PURE__*/React.createElement(StyledSpacer, null), /*#__PURE__*/React.createElement(StyledSelect, {
    required: required,
    size: size,
    value: inputRef.current ? inputRef.current.value : value
  }, /*#__PURE__*/React.createElement(Select, {
    disabled: disabled,
    name: name + "_meridian",
    placeholder: "AM",
    required: required,
    native: true,
    options: meridianOptions,
    value: values.meridian,
    onChange: function onChange(e) {
      handleChange(e, 'meridian');
    }
  }))));
}
TimeInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the input will be not be intractive.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, will disable the first option in each select box.
   */
  disableFirstOption: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * If `true`, the component will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The time format in which `value` is provided.
   */
  fromFormat: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placement of the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The increment value used to create minutes list.
   */
  minutesIncrementStep: PropTypes.oneOf([1, 5]),
  /**
   * Function to handle input change
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Text to describe the field.
   */
  label: PropTypes.node,
  /**
   * HTML `name` attribute of the field.
   */
  name: PropTypes.string.isRequired,
  /**
   *
   */
  required: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The size of the input.
   */
  size: PropTypes.oneOf(['medium', 'small']),
  /**
   * The time format in which new `value` will be returned after selection.
   * @see https://day.js.org/docs/en/display/format#list-of-all-available-formats
   */
  toFormat: PropTypes.string,
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.string
} : {};
TimeInput.defaultProps = {
  disabled: false,
  disableFirstOption: false,
  errorMessage: '',
  fullWidth: false,
  fromFormat: 'HH:mm:ss',
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  minutesIncrementStep: 5,
  required: false,
  showError: false,
  size: 'medium',
  toFormat: 'HH:mm:ss',
  value: ''
};
export default TimeInput;